import { Link, useRouter } from '@tanstack/react-router'
import { Mail, Phone } from 'lucide-react'
import { formatFullName } from '@/lib/utils'
import type { RenderedContact } from './render-data-payload'

/**
 * Clickable contact card rendered inline in the chat transcript for
 * `render_data`. Markup mirrors `dashboard/lead-card.tsx`'s `renderInquiryItem`:
 * a bordered row linking to the contact detail page, hover-preloaded. Contacts
 * have no stage, so there is no badge.
 */
export function ContactEntityCard({ contact }: { contact: RenderedContact }) {
  const router = useRouter()

  const handlePrefetch = () => {
    void router.preloadRoute({ to: '/contacts/$id', params: { id: contact.id } })
  }

  return (
    <Link
      to="/contacts/$id"
      params={{ id: contact.id }}
      data-slot="entity-card"
      data-entity-type="contact"
      className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
      onMouseEnter={handlePrefetch}
    >
      <div className="space-y-1 min-w-0">
        <p className="font-medium text-sm truncate">{formatFullName(contact.firstName, contact.lastName)}</p>
        <div className="space-y-0.5 text-xs text-muted-foreground">
          {contact.email && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Mail className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{contact.email}</span>
            </div>
          )}
          {contact.phoneNumber && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Phone className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{contact.phoneNumber}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}
